import { IsSingleDay, getParamsTime } from "@/pages/dashboard/utils/utils";
import { PERIODTIMEOBJ} from "../../_config/constant";
import { IAccumulatedMetricsFn, ICustomSelectorDimensionConfig, PerformanceTableReqParams, TableProps, SortBy, TLockColumn, TimeType, IMetricMappingItem } from "./types";
import { reverse, uniqBy } from 'lodash-es';
import { history } from "umi";
import { isRateSort as getIsRateSort } from "../PerformanceMetricsTable/components/_utils";
import { ACCUMULATED_METRICS_CONFIG, METRICS_ONLY_BY_DATE } from "./constant";

export const overallPlaytimeFormatter = (params: any, res: any) => {
  const { page, row_count, average = {} } = res || {};
  const pageCount = params?.page_size * page;
  const isLast = pageCount >= row_count && row_count;
  if (isLast) {
    res.aggregation = [{ ...average, date: '', type: 'avg' }];
  }
  return res;
};

// ==================== 指标映射工具函数 ====================

/**
 * 获取需要处理的指标 key 列表
 * @param timeType 时间维度
 * @returns 包含所有 baseKey 和 accumulatedKey 的数组
 */
export const getAccumulatedMetricKeys = (timeType: TimeType): string[] => {
  const config = ACCUMULATED_METRICS_CONFIG[timeType] || [];
  const keys: string[] = [];
  config.forEach((item: IMetricMappingItem) => {
    keys.push(item.baseKey, item.accumulatedKey);
  });
  return keys;
};

/**
 * 根据 metricKey 获取对应的 UI 配置
 * @param metricKey 指标 key
 * @param isAccumulated 是否累计
 * @param timeType 时间维度
 * @returns UI 配置对象
 */
export const getMetricUIConfig = (
  metricKey: string,
  isAccumulated: boolean,
  timeType: TimeType
): { metricKey: string; reqMetricKey: string; enName: string; cnName: string; } | null => {
  const config = ACCUMULATED_METRICS_CONFIG[timeType] || [];
  const item = config.find((c: IMetricMappingItem) => c.baseKey === metricKey || c.accumulatedKey === metricKey);
  if (!item) return null;

  const targetKey = isAccumulated ? item.accumulatedKey : item.baseKey;
  return {
    metricKey: targetKey,
    reqMetricKey: targetKey,
    enName: isAccumulated ? item.enName[1] : item.enName[0],
    cnName: isAccumulated ? item.cnName[1] : item.cnName[0],
  };
};

/**
 * 生成 key 映射表（用于 handleMetricsKeyNotByDate）
 * @param timeType 时间维度
 * @param isAccumulated 是否累计
 * @returns key 映射表
 */
export const getMetricKeyMapping = (
  timeType: TimeType,
  isAccumulated: boolean
): Record<string, string> => {
  const config = ACCUMULATED_METRICS_CONFIG[timeType] || [];
  const mapping: Record<string, string> = {};
  config.forEach((item: IMetricMappingItem) => {
    if (isAccumulated) {
      mapping[item.baseKey] = item.accumulatedKey;
    } else {
      mapping[item.accumulatedKey] = item.baseKey;
    }
  });
  return mapping;
};

// ==================== 重构后的指标处理函数 ====================

export const handleMetricsNotByDate = (componentMetrics: any, isAccumulated: boolean, timeType: string) => {
  const validTimeType = (timeType || 'Day') as TimeType;
  const metricsToRemove = isAccumulated ? componentMetrics.filter((v: any) => METRICS_ONLY_BY_DATE.includes(v?.metricKey)) : [];
  const keyList = getAccumulatedMetricKeys(validTimeType);

  const metrics = componentMetrics?.map((item: any) => {
    if (keyList?.includes(item?.metricKey)) {
      const uiConfig = getMetricUIConfig(item?.metricKey, isAccumulated, validTimeType);
      if (uiConfig) {
        item = {
          ...item,
          ...uiConfig,
          enPercentWidth: isAccumulated ? 190 : 160,
          cnPercentWidth: isAccumulated ? 190 : 160,
          enWidth: isAccumulated ? 190 : 85,
          cnWidth: isAccumulated ? 190 : 85,
        };
      }
    }
    return item;
  }) || [];

  const filteredMetrics = metrics.filter((item: any) => !metricsToRemove.includes(item));

  return filteredMetrics;
};

export const handleMetricsKeyNotByDate = (componentMetrics: any, metricsKey: string[], isAccumulated = false, timeType: string): string[] => {
  const newMetricsKey: string[] = metricsKey;
  const validTimeType = (timeType || 'Day') as TimeType;

  // 使用配置生成映射表
  const metricsToChange = getMetricKeyMapping(validTimeType, isAccumulated);

  const filteredMetricsOnlyByDate = componentMetrics?.filter((metric: any) => METRICS_ONLY_BY_DATE.includes(metric.metricKey)).map((metric: any) => metric.metricKey);

  if (isAccumulated) {
    filteredMetricsOnlyByDate.forEach((metricToRemove: any) => {
      const indexToRemove = newMetricsKey.indexOf(metricToRemove);
      if (indexToRemove !== -1) {
        newMetricsKey.splice(indexToRemove, 1);
      }
    });

    updateMetricKeys(newMetricsKey, metricsToChange);
  } else {
    filteredMetricsOnlyByDate?.forEach((metricToAdd: any) => {
      if (!newMetricsKey.includes(metricToAdd)) {
        newMetricsKey.unshift(metricToAdd);
      }
    });

    updateMetricKeys(newMetricsKey, metricsToChange);
  }

  return newMetricsKey;
};

const updateMetricKeys = (
  metricKeys: string[],
  metricsToUpdate: Record<string, string>
): void => {
  metricKeys.forEach((metricKey, index) => {
    if (Object.prototype.hasOwnProperty.call(metricsToUpdate, metricKey)) {
      metricKeys[index] = metricsToUpdate[metricKey];
    }
  });
};

/**
 * 处理宽表取消by date需要的参数
 * @param param0 filterVal 筛选器参数 isHaveDiff是否有补齐逻辑 componentMetrics配置指标 accumulated宽表维度勾选
 * @returns hasDate 是否取消勾选date isAccumulated 是否累计值计算 metricList配置化指标处理
 */
export const handleAccumulatedMetrics = ({ filterVal, isHaveDiff, componentMetrics, accumulated }: IAccumulatedMetricsFn) => {
  // 是否取消勾选date维度
  const hasDate = accumulated?.includes('date');
  // overview有补齐逻辑，需要用接口请求时间判断是否单日单周单月
  const paramRangeTime = getParamsTime(filterVal, isHaveDiff);
  // 是否单日单周单月
  const isSingle = IsSingleDay({ rangeTime: paramRangeTime, period: PERIODTIMEOBJ[filterVal?.timeType || 'Day'] });
  // 是否累计值计算逻辑
  const isAccumulated = !hasDate && !isSingle;
  // 处理累计值指标转换 因为依赖accumulated，setAccumulated事件拿不到转换指标，需要在事件中判断传入的指标值
  const metricsList = handleMetricsNotByDate(componentMetrics, isAccumulated, filterVal?.timeType);
  return {
    hasDate,
    isAccumulated,
    metricsList,
  };
};

export const getRateSortParams = (
  sortBy: SortBy,
  selectType: string,
): [{ sort_by: string; desc: boolean; }] | [] => {
  if (!sortBy || !selectType) return [];

  const { by, order }: { by?: string; order?: string; } = sortBy || {};

  if (getIsRateSort({ by, order })) {
    return [{
      sort_by: `${by?.replace(selectType, '')}_${selectType}`,
      desc: order === 'desc_by_rate',
    }];
  }

  return [];
};

/* 将单个 SortBy 转为 multi_sort 项 */
const convertSortToParam = (s: SortBy, selectType: string) => ({
  sort_by: s?.by?.replace(selectType, '') || 'date',
  desc: s?.order !== 'asc',
});

/* 获取多字段排序参数
 * lockColumns 锁住列配置
 * columns 表格字段配置
 * currentSort 当前排序，支持单个或数组（多字段排序）
 * selectType 表格值类型：value、dod、sdlw
 * isUseNewSortParams 是否新接口，由以前的排序参数，更改为新的排序参数
 */
export const getMultiSortParams = (lockColumns: TLockColumn[] = [], columns: any[], currentSort: SortBy | SortBy[] | undefined, selectType: string, isUseNewSortParams = false) => {
  const sortList = Array.isArray(currentSort) ? currentSort : (currentSort ? [currentSort] : []);
  const firstSort = sortList[0];
  const isRateSort = getIsRateSort(firstSort);

  if (lockColumns && lockColumns?.length > 0) {
    // 展示的锁住列
    const lockCols = columns?.filter(item => item.lock && item.hasLock);

    if (lockCols?.length > 0) {
      let sorts = lockCols.map(item => ({
        sort_by: item.key,
        desc: item.defaultSort !== 'asc'
      }));

      if (sortList.length > 0) {
        sortList.forEach((sortItem) => {
          if (getIsRateSort(sortItem)) {
            sorts.push(...getRateSortParams(sortItem, selectType));
          } else {
            sorts.push(convertSortToParam(sortItem, selectType));
          }
        });
        // 对锁住列排序时，需去重，按照后一个覆盖前面的原则
        const reversedData = reverse([...sorts]);
        const uniqueData = uniqBy(reversedData, 'sort_by');
        sorts = reverse(uniqueData);
      }
      return sorts;
    }

    if (isUseNewSortParams) {
      if (sortList.length === 0) return [{ sort_by: 'date', desc: true }];
      if (isRateSort) return getRateSortParams(firstSort, selectType);
      return sortList.map((s) => convertSortToParam(s, selectType));
    }
  } else if (isUseNewSortParams) {
    if (sortList.length === 0) return [{ sort_by: 'date', desc: true }];
    if (isRateSort) return getRateSortParams(firstSort, selectType);
    return sortList.map((s) => convertSortToParam(s, selectType));
  } else {
    if (isRateSort) return getRateSortParams(firstSort, selectType);
  }
};

// 格式化Props,主要用于处理url的state传参控制表格的默认选中维度以及表格的默认排序
export const formatProps = (props: TableProps) => {
  const { config } = props || {};
  const { dimensionConfig } = config|| {};
  const historyParam: any = history?.location?.state;
  const urlOtherParams= historyParam ? JSON.parse(historyParam)?.otherParams : undefined;
  const {defaultAccumulation} = urlOtherParams || {};
  if (defaultAccumulation) {
    return {
      ...props,
      config: {
        ...(config || {}),
        dimensionConfig: {
          ...(dimensionConfig || {}),
          defaultAccumulation,
        },
      },
    };
  }
  return {...props};
};

/**
 * 将配置的维度 key 写入 accumulation.custom_accumulation（与历史「维度勾选」能力保持一致）
 * 当传入 options 且用户选择「全部」选项时，按 customSelectorsDimensions 中 isAccumulationWhenAllSelected 配置决定 is_accumulation
 */
export const mergeCustomAccumulationKeys = (
  params: PerformanceTableReqParams,
  includeCustomAccumulationKeys?: string[],
  options?: {
    customFiltersMerge?: Array<{ key: string; filter_list: string[] }>;
    selectorDimensions?: ICustomSelectorDimensionConfig[];
  },
) => {
  if (!includeCustomAccumulationKeys || includeCustomAccumulationKeys.length === 0 || !params.accumulation) {
    return params;
  }
  const accAny = params.accumulation as Record<string, any>;
  const custom = accAny.custom_accumulation || [];
  const merged = [...custom];
  const customFiltersMerge = options?.customFiltersMerge;
  const selectorDimensions = options?.selectorDimensions;

  includeCustomAccumulationKeys.forEach(key => {
    const exists = merged.some((item: { key: string }) => item?.key === key);
    if (!exists) {
      let is_accumulation = true;
      if (customFiltersMerge && selectorDimensions?.length) {
        const dim = selectorDimensions.find(d => d.key === key);
        const selected = customFiltersMerge.find((m: any) => m?.key === key);
        const selectedValue = selected?.filter_list?.[0];
        const isAllOption = dim?.options?.find(o => o.value === selectedValue)?.isAll === true;
        if (isAllOption && dim) {
          is_accumulation = dim.isAccumulationWhenAllSelected === true;
        }
      }
      merged.push({ key, is_accumulation });
    }
  });

  const nextParams = {
    ...(params as any),
    accumulation: {
      ...params.accumulation,
      custom_accumulation: merged,
    },
  } satisfies PerformanceTableReqParams;

  return nextParams;
};

/**
 * customFiltersFromData 配置的维度：勾选分维度时写入 accumulation.custom_accumulation
 * （不依赖全局 setCustomFilterList）
 */
export const mergeDataSourceCustomAccumulationKeys = (
  params: PerformanceTableReqParams,
  accumulated: string[] = [],
  dataSourceCustomFilterKeys?: string[],
) => {
  if (!dataSourceCustomFilterKeys?.length || !accumulated?.length || !params.accumulation) {
    return params;
  }
  const accAny = params.accumulation as Record<string, any>;
  const custom = Array.isArray(accAny.custom_accumulation)
    ? [...accAny.custom_accumulation]
    : [];
  let changed = false;

  dataSourceCustomFilterKeys.forEach(key => {
    if (!accumulated.includes(key)) return;
    if (custom.some((item: { key: string }) => item?.key === key)) return;
    custom.push({ key, is_accumulation: true });
    changed = true;
  });

  if (!changed) {
    return params;
  }

  return {
    ...(params as any),
    accumulation: {
      ...params.accumulation,
      custom_accumulation: custom,
    },
  } satisfies PerformanceTableReqParams;
};

/**
 * 从页面 data 提取自定义筛选器值（支持数组或 { values } 结构）
 */
export const extractDataCustomFilterValues = (
  data: Record<string, unknown> | undefined,
  filterKey: string,
): string[] | undefined => {
  const raw = data?.[filterKey];
  if (!raw) return undefined;
  const values = Array.isArray(raw) ? raw : (raw as { values?: Array<string | number> })?.values;
  if (!values?.length) return undefined;
  return values.map((item) => String(item));
};

/** 生成 data 来源自定义筛选器的 custom_filters_merge 列表 */
export const buildDataSourceCustomFiltersMerge = (
  data: Record<string, unknown> | undefined,
  filterKeys: string[] = [],
): Array<{ key: string; filter_list: string[] }> | undefined => {
  if (!filterKeys?.length) return undefined;
  const mergeList = filterKeys
    .map((key) => {
      const filterList = extractDataCustomFilterValues(data, key);
      return filterList?.length ? { key, filter_list: filterList } : null;
    })
    .filter((item): item is { key: string; filter_list: string[] } => !!item);
  return mergeList.length ? mergeList : undefined;
};

/** 用于监听 data 来源自定义筛选器变化，避免 otherParams 引用变化导致重复请求 */
export const buildDataCustomFiltersRequestKey = (
  data: Record<string, unknown> | undefined,
  filterKeys: string[] = [],
): string => {
  if (!filterKeys?.length) return '';
  return filterKeys
    .map((key) => extractDataCustomFilterValues(data, key)?.join(',') || '')
    .join('|');
};

/**
 * 将 custom_filters_merge（如 selector 选中值）合并进 filters.custom_filters，
 * 并从请求参数中移除 custom_filters_merge 字段
 */
export const mergeCustomFiltersFromOtherParams = (
  params: PerformanceTableReqParams,
  otherParams?: Record<string, any>,
) => {
  const toMerge = otherParams?.custom_filters_merge;
  if (!toMerge || !Array.isArray(toMerge) || toMerge.length === 0) {
    return params;
  }

  const filtersAny = params.filters as Record<string, any>;
  const existingCustomFilters = Array.isArray(filtersAny.custom_filters)
    ? [...filtersAny.custom_filters]
    : [];

  const mergedCustomFilters = [...existingCustomFilters];
  toMerge.forEach((item: { key: string; filter_list: any[]; }) => {
    const idx = mergedCustomFilters.findIndex((f: any) => f?.key === item.key);
    if (idx >= 0) {
      mergedCustomFilters[idx] = item;
    } else {
      mergedCustomFilters.push(item);
    }
  });

  const { custom_filters_merge: _omit, ...restParams } = params as any;

  const nextParams = {
    ...restParams,
    filters: {
      ...filtersAny,
      custom_filters: mergedCustomFilters,
    },
  } satisfies PerformanceTableReqParams;

  return nextParams;
};
